import { localized, msg } from '@lit/localize';
import { css, html, LitElement, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import type { Id, WorkflowVersionEvent } from '../../../api/types';
import '@spectrum-web-components/action-button/sp-action-button.js';
import '@spectrum-web-components/progress-circle/sp-progress-circle.js';
import '@spectrum-web-components/textfield/sp-textfield.js';
import '@spectrum-web-components/icons-workflow/icons/sp-icon-chevron-left.js';
import '@spectrum-web-components/icons-workflow/icons/sp-icon-search.js';
import '@spectrum-web-components/icons-workflow/icons/sp-icon-undo.js';
import {
  createLineDiff,
  filterVersionEvents,
  formatVersionField,
  formatVersionTime,
  formatVersionValue,
  getPrimaryVersionChange,
  getVersionActionLabel,
  getVersionActorInitials,
  getVersionActorName,
  getVersionActorTone,
  getVersionCategoryLabel,
  getVersionChanges,
  getVersionEntityLabel,
  getVersionGroupLabel,
  getVersionHeading,
  groupVersionEvents,
  VERSION_FILTERS,
  VERSION_GROUPS,
  type VersionFilter,
} from './version-history-utils';

@customElement('kv-version-history-panel')
@localized()
export class KvVersionHistoryPanel extends LitElement {
  static styles = css`
    :host {
      display: block;
      height: 100%;
      min-height: 0;
    }
    .versions-panel {
      position: relative;
      box-sizing: border-box;
      container-type: inline-size;
      display: flex;
      flex-direction: column;
      height: 100%;
      min-height: 0;
      overflow: hidden;
      width: 100%;
      border-radius: 0;
      background: transparent;
      padding: 0;
    }
    .versions-header {
      flex-shrink: 0;
    }
    .version-chips {
      display: flex;
      flex-wrap: wrap;
      gap: 5px;
      margin-bottom: 10px;
    }
    .version-chip {
      border: 1px solid var(--color-app-border);
      border-radius: 999px;
      background: var(--color-sidebar-surface);
      color: var(--spectrum-gray-700);
      cursor: pointer;
      font: inherit;
      font-size: 11px;
      line-height: 1.4;
      padding: 3px 9px;
      white-space: nowrap;
    }
    .version-chip:hover {
      background: var(--color-sidebar-surface-hover);
      color: var(--spectrum-gray-900);
    }
    .version-chip.selected {
      background: var(--color-sidebar-selected-bg);
      border-color: var(--color-accent-border);
      color: var(--color-sidebar-selected-border);
    }
    .version-search {
      margin-bottom: 10px;
      width: 100%;
    }
    .versions-rule,
    .version-entry-divider {
      height: 1px;
      background: var(--color-app-border);
      flex-shrink: 0;
    }
    .version-entry-divider {
      margin: 0;
    }
    .versions-list {
      flex: 1;
      min-height: 0;
      overflow-y: auto;
      scrollbar-width: thin;
    }
    .version-group-label {
      color: var(--spectrum-gray-600);
      font-size: 10px;
      font-weight: 600;
      letter-spacing: 0.06em;
      padding: 12px 0 4px;
      text-transform: uppercase;
    }
    .version-entry {
      border: 0;
      border-left: 2px solid transparent;
      background: transparent;
      box-sizing: border-box;
      color: inherit;
      cursor: pointer;
      display: block;
      font: inherit;
      padding: 8px;
      border-radius: 8px;
      text-align: left;
      transition: background 0.1s ease;
      width: 100%;
    }
    .version-entry:hover,
    .version-entry.selected {
      background: var(--color-sidebar-surface-hover);
    }
    .version-entry.selected {
      border-left-color: var(--color-sidebar-selected-border);
      background: var(--color-sidebar-selected-bg);
    }
    .version-entry:hover .version-restore-inline,
    .version-entry.selected .version-restore-inline {
      opacity: 1;
    }
    .version-row-primary {
      align-items: center;
      display: flex;
      gap: 6px;
      margin-bottom: 3px;
      min-width: 0;
    }
    .version-field {
      color: var(--spectrum-gray-900);
      flex: 1;
      font-size: 12px;
      font-weight: 600;
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .version-entry-time {
      color: var(--spectrum-gray-600);
      flex-shrink: 0;
      font-size: 11px;
      margin-left: auto;
      padding-left: 6px;
      white-space: nowrap;
    }
    .version-row-change {
      align-items: center;
      display: flex;
      gap: 4px;
      margin-bottom: 4px;
      min-width: 0;
    }
    .version-old,
    .version-new {
      flex: 0 1 auto;
      font-size: 11px;
      max-width: min(42%, 160px);
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .version-old {
      color: var(--spectrum-gray-600);
      text-decoration: line-through;
    }
    .version-new {
      color: var(--spectrum-green-700);
    }
    .version-arrow,
    .version-subtext {
      color: var(--spectrum-gray-600);
      font-size: 11px;
    }
    .version-row-user {
      align-items: center;
      display: flex;
      gap: 8px;
      justify-content: space-between;
      min-width: 0;
    }
    .version-user {
      align-items: center;
      display: flex;
      gap: 5px;
      min-width: 0;
    }
    .version-avatar,
    .version-avatar-detail {
      align-items: center;
      border-radius: 50%;
      display: flex;
      flex-shrink: 0;
      font-weight: 600;
      justify-content: center;
    }
    .version-avatar {
      font-size: 9px;
      height: 16px;
      width: 16px;
    }
    .version-avatar-detail {
      font-size: 10px;
      height: 20px;
      width: 20px;
    }
    .version-avatar.tone-0,
    .version-avatar-detail.tone-0 {
      background: #b5d4f4;
      color: #185fa5;
    }
    .version-avatar.tone-1,
    .version-avatar-detail.tone-1 {
      background: #9fe1cb;
      color: #0f6e56;
    }
    .version-avatar.tone-2,
    .version-avatar-detail.tone-2 {
      background: #fac775;
      color: #854f0b;
    }
    .version-avatar.tone-3,
    .version-avatar-detail.tone-3 {
      background: #cecbf6;
      color: #534ab7;
    }
    .version-avatar.tone-4,
    .version-avatar-detail.tone-4 {
      background: #f5c4b3;
      color: #993c1d;
    }
    .version-user-name {
      color: var(--spectrum-gray-700);
      font-size: 11px;
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .version-restore-inline {
      flex-shrink: 0;
      opacity: 0;
      transition: opacity 0.15s ease;
    }
    .version-empty {
      color: var(--spectrum-gray-600);
      font-size: 12px;
      padding: 14px 0;
    }
    .version-load-more {
      border-top: 1px solid var(--color-app-border);
      display: flex;
      justify-content: center;
      padding: 10px 0 14px;
    }
    .version-detail {
      background: var(--color-sidebar-surface);
      display: flex;
      flex-direction: column;
      inset: 0;
      min-height: 0;
      position: absolute;
      transform: translateX(100%);
      transition: transform 0.2s cubic-bezier(0.4, 0, 0.2, 1);
      z-index: 10;
    }
    .version-detail.open {
      transform: translateX(0);
    }
    .version-detail-header {
      align-items: center;
      border-bottom: 1px solid var(--color-app-border);
      display: flex;
      flex-shrink: 0;
      gap: 8px;
      min-height: 48px;
      padding: 10px 14px;
    }
    .version-detail-title {
      color: var(--spectrum-gray-900);
      flex: 1;
      font-size: 13px;
      font-weight: 600;
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .version-detail-title small {
      color: var(--spectrum-gray-600);
      display: block;
      font-size: 11px;
      font-weight: 400;
      margin-top: 2px;
    }
    .version-detail-meta {
      border-bottom: 1px solid var(--color-app-border);
      display: flex;
      flex-direction: column;
      flex-shrink: 0;
      gap: 6px;
      padding: 10px 14px;
    }
    .version-meta-row {
      align-items: center;
      display: flex;
      gap: 0;
    }
    .version-meta-label {
      color: var(--spectrum-gray-600);
      flex-shrink: 0;
      font-size: 11px;
      width: 76px;
    }
    .version-meta-value {
      align-items: center;
      color: var(--spectrum-gray-900);
      display: flex;
      font-size: 12px;
      gap: 5px;
      min-width: 0;
    }
    .version-detail-body {
      flex: 1;
      min-height: 0;
      overflow-y: auto;
      padding: 14px;
    }
    .version-diff-field {
      color: var(--spectrum-gray-700);
      font-size: 11px;
      font-weight: 600;
      margin-bottom: 8px;
    }
    .version-diff-sides {
      display: grid;
      gap: 8px;
      grid-template-columns: 1fr 1fr;
      margin-bottom: 12px;
      min-width: 0;
    }
    .version-diff-side-heading {
      border-radius: 4px;
      font-size: 10px;
      font-weight: 600;
      letter-spacing: 0.05em;
      margin-bottom: 4px;
      padding: 3px 8px;
      text-transform: uppercase;
    }
    .version-diff-side-heading.before {
      background: #fcebeb;
      color: #a32d2d;
    }
    .version-diff-side-heading.after {
      background: #eaf3de;
      color: #3b6d11;
    }
    .version-diff-value {
      background: var(--spectrum-gray-100);
      border: 1px solid var(--color-app-border);
      border-radius: 6px;
      color: var(--spectrum-gray-900);
      font-size: 12px;
      line-height: 1.5;
      min-height: 36px;
      padding: 8px 10px;
      white-space: pre-wrap;
      word-break: break-word;
    }
    .version-diff-value.before {
      background: #fcebeb;
      border-color: #f7c1c1;
      color: #a32d2d;
      text-decoration: line-through;
    }
    .version-diff-value.after {
      background: #eaf3de;
      border-color: #c0dd97;
      color: #3b6d11;
    }
    .version-diff-code {
      border: 1px solid var(--color-app-border);
      border-radius: 6px;
      margin-bottom: 12px;
      overflow: hidden;
    }
    .version-diff-line {
      align-items: baseline;
      display: flex;
      font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
      font-size: 11px;
      gap: 6px;
      line-height: 1.7;
      padding: 2px 10px;
      word-break: break-all;
    }
    .version-diff-line.removed {
      background: #fcebeb;
      color: #a32d2d;
    }
    .version-diff-line.added {
      background: #eaf3de;
      color: #3b6d11;
    }
    .version-diff-line.context {
      color: var(--spectrum-gray-700);
    }
    .version-context {
      color: var(--spectrum-gray-600);
      font-size: 12px;
    }
    @container (max-width: 360px) {
      .versions-header,
      .version-detail-header,
      .version-detail-meta {
        padding-inline: 10px;
      }
      .version-entry {
        padding-inline: 10px;
      }
      .version-entry-divider {
        margin-inline: 10px;
      }
      .version-row-primary {
        align-items: flex-start;
        flex-wrap: wrap;
      }
      .version-field {
        flex-basis: calc(100% - 58px);
      }
      .version-entry-time {
        margin-left: 0;
        padding-left: 0;
      }
      .version-row-change {
        flex-wrap: wrap;
      }
      .version-old,
      .version-new {
        max-width: calc(50% - 14px);
      }
      .version-restore-inline {
        opacity: 1;
      }
      .version-detail-body {
        padding: 10px;
      }
      .version-diff-sides {
        grid-template-columns: 1fr;
      }
    }
    @container (min-width: 460px) {
      .version-old,
      .version-new {
        max-width: min(44%, 220px);
      }
    }
  `;

  @property({ attribute: false }) accessor events: WorkflowVersionEvent[] = [];
  @property({ type: Boolean }) accessor loading = false;
  @property({ type: Boolean }) accessor loadingMore = false;
  @property({ type: Boolean }) accessor hasMore = false;
  @property({ attribute: false }) accessor restoringVersionId: Id | null = null;

  @state() private accessor filter: VersionFilter = 'all';
  @state() private accessor search = '';
  @state() private accessor selectedEventId: Id | null = null;

  protected willUpdate(changed: Map<string, unknown>) {
    if (!changed.has('events')) return;
    if (!this.events.some((event) => event.id === this.selectedEventId)) {
      this.selectedEventId = null;
    }
  }

  private dispatchRestore(event: WorkflowVersionEvent) {
    this.dispatchEvent(
      new CustomEvent<{ event: WorkflowVersionEvent }>('restore-version', {
        detail: { event },
        bubbles: true,
        composed: true,
      })
    );
  }

  private dispatchLoadMore() {
    this.dispatchEvent(
      new CustomEvent('load-more-versions', {
        bubbles: true,
        composed: true,
      })
    );
  }

  private onSearchInput = (event: Event) => {
    this.search = (event.target as HTMLInputElement).value;
  };

  private renderAvatar(event: WorkflowVersionEvent, detail = false) {
    return html`<span
      class="version-avatar ${detail ? 'version-avatar-detail' : ''} tone-${getVersionActorTone(event)}"
      aria-hidden="true"
    >
      ${getVersionActorInitials(event)}
    </span>`;
  }

  private renderChangeSummary(event: WorkflowVersionEvent) {
    const change = getPrimaryVersionChange(event);
    if (!change) {
      return html`<span class="version-subtext">
        ${getVersionActionLabel(event)} ${getVersionEntityLabel(event)}
      </span>`;
    }
    return html`
      <span class="version-old">${formatVersionValue(change.from)}</span>
      <span class="version-arrow">-></span>
      <span class="version-new">${formatVersionValue(change.to)}</span>
    `;
  }

  private renderEntry(event: WorkflowVersionEvent) {
    const selected = this.selectedEventId === event.id;
    const isRestoring = this.restoringVersionId === event.id;
    return html`
      <div
        class=${`version-entry ${selected ? 'selected' : ''}`}
        role="button"
        tabindex="0"
        @click=${() => {
          this.selectedEventId = event.id;
        }}
        @keydown=${(keyEvent: KeyboardEvent) => {
          if (keyEvent.key !== 'Enter' && keyEvent.key !== ' ') return;
          keyEvent.preventDefault();
          this.selectedEventId = event.id;
        }}
      >
        <span class="version-row-primary">
          <span class="version-field">${getVersionHeading(event)}</span>
          <span class="version-entry-time">${formatVersionTime(event, true)}</span>
        </span>
        <span class="version-row-change">${this.renderChangeSummary(event)}</span>
        <span class="version-row-user">
          <span class="version-user">
            ${this.renderAvatar(event)}
            <span class="version-user-name">${getVersionActorName(event)}</span>
          </span>
          <sp-action-button
            class="version-restore-inline"
            size="s"
            quiet
            ?disabled=${this.restoringVersionId !== null}
            @click=${(clickEvent: Event) => {
              clickEvent.stopPropagation();
              this.dispatchRestore(event);
            }}
          >
            <sp-icon-undo slot="icon"></sp-icon-undo>
            ${isRestoring ? msg('Restoring') : msg('Rollback')}
          </sp-action-button>
        </span>
      </div>
    `;
  }

  private renderDiffValue(
    label: string,
    value: unknown,
    tone: 'before' | 'after'
  ) {
    return html`
      <div>
        <div class=${`version-diff-side-heading ${tone}`}>${label}</div>
        <div class=${`version-diff-value ${tone}`}>
          ${formatVersionValue(value)}
        </div>
      </div>
    `;
  }

  private renderTextDiff(before: string, after: string) {
    return html`
      <div class="version-diff-code">
        ${createLineDiff(before, after).map((line) => {
          const prefix =
            line.type === 'added' ? '+ ' : line.type === 'removed' ? '- ' : '';
          return html`<div class=${`version-diff-line ${line.type}`}>
            ${prefix}${line.text}
          </div>`;
        })}
      </div>
    `;
  }

  private renderDiff(event: WorkflowVersionEvent) {
    const entries = Object.entries(getVersionChanges(event));
    if (!entries.length) {
      return html`<p class="version-context">
        ${getVersionActionLabel(event)} ${getVersionEntityLabel(event)}.
      </p>`;
    }
    return html`${entries.map(([field, change]) => {
      const before = formatVersionValue(change.from);
      const after = formatVersionValue(change.to);
      const multiline = before.includes('\n') || after.includes('\n');
      return html`
        <section>
          <h5 class="version-diff-field">${formatVersionField(field)}</h5>
          ${
            multiline
              ? this.renderTextDiff(before, after)
              : html`<div class="version-diff-sides">
                ${this.renderDiffValue(msg('Before'), change.from, 'before')}
                ${this.renderDiffValue(msg('After'), change.to, 'after')}
              </div>`
          }
        </section>
      `;
    })}`;
  }

  private renderDetail(event: WorkflowVersionEvent | undefined) {
    return html`
      <aside class=${`version-detail ${event ? 'open' : ''}`}>
        ${
          event
            ? html`
              <div class="version-detail-header">
                <sp-action-button
                  quiet
                  size="s"
                  aria-label=${msg('Back to version history')}
                  @click=${() => {
                    this.selectedEventId = null;
                  }}
                >
                  <sp-icon-chevron-left slot="icon"></sp-icon-chevron-left>
                </sp-action-button>
                <div class="version-detail-title">
                  <span>${getVersionHeading(event)}</span>
                  <small>${getVersionActionLabel(event)}</small>
                </div>
                <sp-action-button
                  quiet
                  size="s"
                  ?disabled=${this.restoringVersionId !== null}
                  @click=${() => this.dispatchRestore(event)}
                >
                  <sp-icon-undo slot="icon"></sp-icon-undo>
                  ${
                    this.restoringVersionId === event.id
                      ? msg('Restoring')
                      : msg('Rollback')
                  }
                </sp-action-button>
              </div>
              <div class="version-detail-meta">
                <div class="version-meta-row">
                  <span class="version-meta-label">${msg('Entity')}</span>
                  <span class="version-meta-value">
                    ${getVersionEntityLabel(event)}
                  </span>
                </div>
                <div class="version-meta-row">
                  <span class="version-meta-label">${msg('Changed by')}</span>
                  <span class="version-meta-value">
                    ${this.renderAvatar(event, true)}
                    ${getVersionActorName(event)}
                  </span>
                </div>
                <div class="version-meta-row">
                  <span class="version-meta-label">${msg('Timestamp')}</span>
                  <span class="version-meta-value">
                    ${formatVersionTime(event)}
                  </span>
                </div>
              </div>
              <div class="version-detail-body">${this.renderDiff(event)}</div>
            `
            : nothing
        }
      </aside>
    `;
  }

  render() {
    const events = filterVersionEvents(
      this.events ?? [],
      this.filter,
      this.search
    );
    const selectedEvent =
      events.find((event) => event.id === this.selectedEventId) ??
      this.events.find((event) => event.id === this.selectedEventId);
    const grouped = groupVersionEvents(events);
    const hasFilters = this.filter !== 'all' || this.search.trim().length > 0;

    return html`
      <section class="versions-panel">
        <header class="versions-header">
          <div class="version-chips" aria-label=${msg('Version filters')}>
            ${VERSION_FILTERS.map((filter) => {
              const selected = this.filter === filter;
              const label =
                filter === 'all' ? msg('All') : getVersionCategoryLabel(filter);
              return html`
                <button
                  class=${`version-chip ${selected ? 'selected' : ''}`}
                  type="button"
                  aria-pressed=${selected ? 'true' : 'false'}
                  @click=${() => {
                    this.filter = filter;
                  }}
                >
                  ${label}
                </button>
              `;
            })}
          </div>
          <sp-textfield
            class="version-search"
            quiet
            placeholder=${msg('Search changes')}
            .value=${this.search}
            @input=${this.onSearchInput}
          >
            <sp-icon-search slot="icon"></sp-icon-search>
          </sp-textfield>
        </header>
        <div class="versions-rule"></div>
        ${
          this.loading
            ? html`<div class="version-empty">
              <sp-progress-circle
                indeterminate
                label=${msg('Loading versions')}
              ></sp-progress-circle>
            </div>`
            : events.length
              ? html`
                <div class="versions-list">
                  ${VERSION_GROUPS.map((group) => {
                    const items = grouped.get(group) ?? [];
                    if (!items.length) return nothing;
                    return html`
                      <h4 class="version-group-label">
                        ${getVersionGroupLabel(group)}
                      </h4>
                      ${items.map(
                        (item, index) => html`
                          ${
                            index > 0
                              ? html`<div class="version-entry-divider"></div>`
                              : nothing
                          }
                          ${this.renderEntry(item)}
                        `
                      )}
                    `;
                  })}
                </div>
              `
              : html`<p class="version-empty">
                ${
                  hasFilters
                    ? msg('No matching version changes.')
                    : msg('No version changes yet.')
                }
              </p>`
        }
        ${
          this.hasMore && !this.loading
            ? html`
              <div class="version-load-more">
                <sp-action-button
                  quiet
                  ?disabled=${this.loadingMore}
                  @click=${() => this.dispatchLoadMore()}
                >
                  ${
                    this.loadingMore
                      ? html`<sp-progress-circle
                        slot="icon"
                        indeterminate
                        size="s"
                        label=${msg('Loading more versions')}
                      ></sp-progress-circle>`
                      : nothing
                  }
                  ${this.loadingMore ? msg('Loading more') : msg('Load more')}
                </sp-action-button>
              </div>
            `
            : nothing
        }
        ${this.renderDetail(selectedEvent)}
      </section>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'kv-version-history-panel': KvVersionHistoryPanel;
  }
}
